import React from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { SearchQuery } from "shared/data/reducers/types/storage";

import Checkbox from "@components/UI/Checkbox";
import FormattedMessage from "@components/UI/FormattedMessage";

import FilterOnlyAction from "./FilterOnlyAction";
import {
  getDefaultSelectedCheckboxValues,
  getMaxNumTransfers,
} from "./helpers";
import { State } from "./types";

type Props = {
  query: SearchQuery;
  activeFilter: State["activeFilter"];
  changeDefaultSelectedCheckboxFilter: (
    key: string,
    code: string,
    allValues: string[]
  ) => void;
  changeOnlyCheckboxFilter: (key: string, code: string) => void;
};

const FilterBox3 = ({
  query,
  activeFilter,
  changeDefaultSelectedCheckboxFilter,
  changeOnlyCheckboxFilter,
}: Props) => {
  const {
    airports,
    airportsFrom,
    airportsTo,
    airportsTransfers,
    maxNumTransfers,
  } = useSelector((appState: AppState) => ({
    airports: appState.search.airports,
    airportsFrom: appState.search.airportsFrom,
    airportsTo: appState.search.airportsTo,
    airportsTransfers: appState.search.airportsTransfers,
    maxNumTransfers: appState.search.maxNumTransfers,
  }));

  const getDefaultSelectedChecked = (
    key: string,
    airportCode: string,
    airportCodes: string[]
  ) =>
    getDefaultSelectedCheckboxValues(query, key, airportCodes).includes(
      airportCode
    );

  const getLabel = (airportCode: string) => {
    return `${airportCode} - ${
      airports[airportCode] !== undefined ? airports[airportCode].$t : null
    } `;
  };

  const keyFrom = `airports_${activeFilter}_from`;
  const keyTo = `airports_${activeFilter}_to`;
  const keyTransfers = `airports_${activeFilter}_transfers`;
  const airportCodesFrom = airportsFrom[activeFilter] || [];
  const airportCodesTo = airportsTo[activeFilter] || [];
  const airportCodesTransfers = airportsTransfers[activeFilter] || [];
  const transferAirportsDisabled =
    getMaxNumTransfers(query, maxNumTransfers) === 0;

  return (
    <div className="filters-box">
      <span className="filters-label">
        <FormattedMessage id="Filters.airport" />:
      </span>
      <span className="filters-label-airports">
        <FormattedMessage id="Filters.from" />
      </span>
      <div id="airports-from-container">
        {airportCodesFrom.map((airportCode) => (
          <div key={airportCode} className="filter-option-row">
            <Checkbox
              id="filter-checkbox-airports-from"
              customContainerClass="airportsCheckboxContainer filter-option-checkbox"
              onClick={() =>
                changeDefaultSelectedCheckboxFilter(
                  keyFrom,
                  airportCode,
                  airportCodesFrom
                )
              }
              checked={getDefaultSelectedChecked(
                keyFrom,
                airportCode,
                airportCodesFrom
              )}
              customLabelClass="airportsCheckboxLabel filter-option-checkbox-label"
              label={
                <span className="filter-option-label-text">
                  {getLabel(airportCode)}
                </span>
              }
            />
            <FilterOnlyAction
              label={getLabel(airportCode)}
              onClick={() => changeOnlyCheckboxFilter(keyFrom, airportCode)}
            />
          </div>
        ))}
      </div>
      <span className="filters-label-airports">
        <FormattedMessage id="Filters.to" />
      </span>
      <div id="airports-to-container">
        {airportCodesTo.map((airportCode) => (
          <div key={airportCode} className="filter-option-row">
            <Checkbox
              id="filter-checkbox-airports-to"
              customContainerClass="airportsCheckboxContainer filter-option-checkbox"
              onClick={() =>
                changeDefaultSelectedCheckboxFilter(
                  keyTo,
                  airportCode,
                  airportCodesTo
                )
              }
              checked={getDefaultSelectedChecked(
                keyTo,
                airportCode,
                airportCodesTo
              )}
              customLabelClass="airportsCheckboxLabel filter-option-checkbox-label"
              label={
                <span className="filter-option-label-text">
                  {getLabel(airportCode)}
                </span>
              }
            />
            <FilterOnlyAction
              label={getLabel(airportCode)}
              onClick={() => changeOnlyCheckboxFilter(keyTo, airportCode)}
            />
          </div>
        ))}
      </div>
      <span
        className={`filters-label-airports ${
          transferAirportsDisabled ? "filters-label-airports--disabled" : ""
        }`}
      >
        <FormattedMessage id="Filters.through" />
      </span>
      <div
        id="airports-transfers-container"
        aria-disabled={transferAirportsDisabled || undefined}
      >
        {airportCodesTransfers.map((airportCode) => (
          <Checkbox
            key={airportCode}
            customContainerClass="airportsCheckboxContainer"
            onClick={() =>
              changeDefaultSelectedCheckboxFilter(
                keyTransfers,
                airportCode,
                airportCodesTransfers
              )
            }
            checked={getDefaultSelectedChecked(
              keyTransfers,
              airportCode,
              airportCodesTransfers
            )}
            customLabelClass="airportsCheckboxLabel"
            disabled={transferAirportsDisabled}
            label={getLabel(airportCode)}
          />
        ))}
      </div>
    </div>
  );
};

export default FilterBox3;
